import { buildSelectionKey, isSelectableItem } from './selectionKey';
import toSelectionEntry from './toSelectionEntry';
import { EMPTY_SELECTION } from './selectionState';
import { getEntitySelectionContext, resolveContextForEntities } from './selectionContext';
import { getGridShiftRangeItems, resolveGridShiftRange } from './selectionRange';

const toEntry = (item) => toSelectionEntry(item);

export const getSearchRangeSelectionPlan = (selection, items, { item, value, isRange }) => {
  const rangeItems = getGridShiftRangeItems(selection, items, { item, isRange });

  if (!rangeItems?.length) {
    return null;
  }

  if (!value) {
    const removeKeys = rangeItems
      .map((rangeItem) => buildSelectionKey(rangeItem))
      .filter((rangeKey) => selection.byKey[rangeKey]);

    return {
      removeKeys,
      lastClickedKey: selection.lastClickedKey,
    };
  }

  const addEntries = rangeItems
    .map((rangeItem) => toEntry(rangeItem))
    .filter((entry) => !selection.byKey[entry.key]);

  if (!addEntries.length) {
    return {
      lastClickedKey: selection.lastClickedKey,
    };
  }

  return {
    addEntries,
    lastClickedKey: selection.lastClickedKey,
  };
};

/**
 * Pure reducer helper for the products grid's checkbox selection. Range math is done here
 * (against the full loaded `items` array, not the DOM) because the grid is virtualized — a
 * shift-click anchor from hundreds of rows up is very likely unmounted.
 *
 * @param {{ keys: string[], byKey: object, lastClickedKey: string|null }} selection
 * @param {object[]} items - state.products.search.items (all loaded rows, not just visible ones)
 * @param {{ item: object, value: boolean, isRange?: boolean }} action
 * @returns {{ keys: string[], byKey: object, lastClickedKey: string|null }}
 */
export default function applySelection(selection, items, { item, value, isRange }) {
  if (!isSelectableItem(item)) return selection;

  const key = buildSelectionKey(item);

  if (isRange && selection.lastClickedKey) {
    const range = resolveGridShiftRange(items, selection.lastClickedKey, key);

    // Anchor not found among currently-loaded selectable rows (e.g. filtered out) — fall back
    // to a plain single toggle instead of guessing a range.
    if (!range) {
      return applySelection(selection, items, { item, value, isRange: false });
    }

    const byKey = { ...selection.byKey };
    const keySet = new Set(selection.keys);

    range.forEach((rangeItem) => {
      const rangeKey = buildSelectionKey(rangeItem);
      if (value) {
        if (!keySet.has(rangeKey)) {
          keySet.add(rangeKey);
          byKey[rangeKey] = toEntry(rangeItem);
        }
      } else if (keySet.has(rangeKey)) {
        keySet.delete(rangeKey);
        delete byKey[rangeKey];
      }
    });

    return {
      keys: [...keySet],
      byKey,
      lastClickedKey: selection.lastClickedKey,
      isComplete: false,
      context: keySet.size ? selection.context : null,
      source: keySet.size ? (selection.source ?? 'search') : null,
    };
  }

  const hasKey = Object.prototype.hasOwnProperty.call(selection.byKey, key);

  if (value === hasKey) {
    // idempotent — still move the anchor for a plain click (not for a range click, per above)
    return {
      ...selection,
      lastClickedKey: key,
      isComplete: false,
    };
  }

  if (value) {
    return {
      keys: [...selection.keys, key],
      byKey: { ...selection.byKey, [key]: toEntry(item) },
      lastClickedKey: key,
      isComplete: false,
      context: selection.context || getEntitySelectionContext(item),
      source: selection.source ?? 'search',
    };
  }

  const byKey = { ...selection.byKey };
  delete byKey[key];
  const keys = selection.keys.filter((k) => k !== key);
  return {
    keys,
    byKey,
    lastClickedKey: key,
    isComplete: false,
    context: keys.length ? selection.context : null,
    source: keys.length ? selection.source : null,
  };
}

export function selectAllLoaded(selection, items) {
  const byKey = { ...selection.byKey };
  const keys = [...selection.keys];

  items.forEach((item) => {
    if (!isSelectableItem(item)) return;
    const key = buildSelectionKey(item);
    if (Object.prototype.hasOwnProperty.call(byKey, key)) return;
    keys.push(key);
    byKey[key] = toEntry(item);
  });

  return {
    keys,
    byKey,
    lastClickedKey: selection.lastClickedKey,
    isComplete: false,
    context: keys.length ? selection.context : null,
    source: keys.length ? (selection.source ?? 'search') : null,
  };
}

/**
 * Replaces the current selection with the given selectable records (Select All).
 * Non-selectable rows are skipped. `isComplete` disables Select All — only pass true
 * when every search hit was typed, so an unknown id is not silently dropped.
 *
 * @param {{ type: string, _id: string }[]} items
 * @param {{ isComplete?: boolean, source?: string }} [options]
 */
export function materializeSelection(items, { isComplete = true, source, recordFilter } = {}) {
  const byKey = {};
  const keys = [];

  items.forEach((item) => {
    if (!isSelectableItem(item)) return;
    const key = buildSelectionKey(item);
    keys.push(key);
    byKey[key] = toEntry(item);
  });

  return {
    keys,
    byKey,
    lastClickedKey: null,
    isComplete,
    context: keys.length ? resolveContextForEntities(Object.values(byKey), recordFilter) : null,
    source: keys.length ? (source ?? 'search') : null,
  };
}

export function deselectAll() {
  return EMPTY_SELECTION;
}

export function pruneSelection(selection, keysToRemove) {
  if (!keysToRemove.length) return selection;
  const removeSet = new Set(keysToRemove);
  if (!selection.keys.some((key) => removeSet.has(key))) return selection;

  const byKey = { ...selection.byKey };
  keysToRemove.forEach((key) => delete byKey[key]);
  const keys = selection.keys.filter((key) => !removeSet.has(key));
  return {
    keys,
    byKey,
    lastClickedKey: removeSet.has(selection.lastClickedKey) ? null : selection.lastClickedKey,
    isComplete: false,
    context: keys.length ? selection.context : null,
    source: keys.length ? selection.source : null,
  };
}

export { EMPTY_SELECTION };
